import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import AdminSidebar from "../../component/AdminComp/AdminSidebar";
import AdminTopbar from "../../component/AdminComp/AdminTopbar";
import Swal from "sweetalert2";
import axios from "axios";
import { baseurl } from "../../Baseurl";
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const BlogEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [thumbnailFile, setThumbnailFile] = useState(null);
const editorRef = useRef(null); // ref for CKEditor container
  const editorInstance = useRef(null); 


  const [formData, setFormData] = useState({
    category_id: "",
    title: "",
    slug: "",
    content: "",
    author_name: "",
    is_published: false,
    title_tag: "",
    meta_description: "",
    meta_keywords: "",
    schema_markup: "",
    alt_img: "",
  });

   useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const [catRes, blogRes] = await Promise.all([
          axios.get(`${baseurl}GetAllBlogCategories`),
          axios.get(`${baseurl}GetBlogById/${id}`),
        ]);

        setCategories(catRes.data.categories);

        const blog = blogRes.data;
        setFormData({
          category_id: blog.category_id.toString(),
          title: blog.title,
          slug: blog.slug,
          content: blog.content,
          author_name: blog.author_name || "",
          is_published: blog.is_published,
          title_tag: blog.title_tag || "",
          meta_description: blog.meta_description || "",
          meta_keywords: blog.meta_keywords || "",
          schema_markup: blog.schema_markup || "",
          alt_img: blog.alt_img || "",
        });

        // ✅ Initialize CKEditor after data is loaded
        if (window.CKEDITOR && editorRef.current) {
          if (editorInstance.current) {
            editorInstance.current.destroy();
          }

          window.CKEDITOR.ClassicEditor.create(editorRef.current, {
            toolbar: {
              items: [
                "undo",
                "redo",
                "|",
                "heading",
                "|",
                "bold",
                "italic",
                "underline",
                "|",
                "fontColor",
                "fontBackgroundColor",
                "|",
                "link",
                "|",
                "bulletedList",
                "numberedList",
                "|",
                "insertTable",
                "|",
                "sourceEditing",
              ],
            },
            heading: {
              options: [
                { model: "paragraph", title: "Paragraph", class: "ck-heading_paragraph" },
                { model: "heading1", view: "h1", title: "Heading 1", class: "ck-heading_heading1" },
                { model: "heading2", view: "h2", title: "Heading 2", class: "ck-heading_heading2" },
                { model: "heading3", view: "h3", title: "Heading 3", class: "ck-heading_heading3" },
                { model: "heading4", view: "h4", title: "Heading 4", class: "ck-heading_heading4" },
                { model: "heading5", view: "h5", title: "Heading 5", class: "ck-heading_heading5" },
                { model: "heading6", view: "h6", title: "Heading 6", class: "ck-heading_heading6" },
              ],
            },
            removePlugins: [
              "CKBox",
              "CKFinder",
              "EasyImage",
              "RealTimeCollaborativeComments",
              "RealTimeCollaborativeTrackChanges",
              "RealTimeCollaborativeRevisionHistory",
              "PresenceList",
              "Comments",
              "TrackChanges",
              "TrackChangesData",
              "RevisionHistory",
              "Pagination",
              "WProofreader",
            ],
          }).then((editor) => {
            editorInstance.current = editor;
            editor.setData(blog.content || "");
            editor.model.document.on("change:data", () => {
              setFormData((prev) => ({ ...prev, content: editor.getData() }));
            });
          });
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        Swal.fire("Error", "Failed to load blog data", "error");
      }
    };

    fetchInitialData();

    return () => {
      if (editorInstance.current) {
        editorInstance.current.destroy();
      }
    };
  }, [id]);



  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
  
    // Slugify helper
    const slugify = (text) =>
      text
        .toString()
        .toLowerCase()
        .trim()
        .replace(/\s+/g, "-")
        .replace(/[^a-z0-9-]/g, "")
        .replace(/--+/g, "-");
  
    setFormData((prev) => {
      const updatedData = {
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      };
  
      // If changing title, also update slug
      if (name === "title") {
        updatedData.slug = slugify(value);
      }
  
      return updatedData;
    });
  };

  const handleFileChange = (e) => {
    setThumbnailFile(e.target.files[0]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const data = new FormData();
      data.append("category_id", formData.category_id);
      data.append("title", formData.title);
      data.append("slug", formData.slug);
      data.append("content", formData.content);
      data.append("author_name", formData.author_name);
      data.append("is_published", formData.is_published);
      data.append("id", id);

      data.append("title_tag", formData.title_tag);
      data.append("meta_description", formData.meta_description);
      data.append("meta_keywords", formData.meta_keywords);
      data.append("schema_markup", formData.schema_markup);
      data.append("alt_img", formData.alt_img);


      if (thumbnailFile) {
        data.append("thumbnail", thumbnailFile);
      }

      const res = await axios.post(`${baseurl}UpdateBlog`, data, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (res.status === 200) {
        Swal.fire("Updated!", "Blog updated successfully.", "success");
        navigate("/admin/blog-list");
      }
    } catch (error) {
      console.error("Error updating blog:", error);
      Swal.fire("Error", "Failed to update blog", "error");
    }
  };

  return (
    <div className="App" style={{ display: "flex" }}>
      <AdminSidebar />
      <div className="content" style={{ flexGrow: 1, padding: "20px" }}>
        <AdminTopbar />
        <div className="container card">
          <h4 className="text-start mb-4">Edit Blog</h4>
          <form onSubmit={handleSubmit}>
            <div className="mb-3 text-start">
              <label className="form-label">Blog Category</label>
              <select
                className="form-control"
                name="category_id"
                value={formData.category_id}
                onChange={handleChange}
                required
              >
                <option value="">Select Blog Category</option>
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="mb-3 text-start">
              <label className="form-label">Title</label>
              <input
                type="text"
                className="form-control"
                name="title"
                value={formData.title}
                onChange={handleChange}
                required
              />
            </div>

            <div className="mb-3 text-start">
              <label className="form-label">Slug</label>
              <input
                type="text"
                className="form-control"
                name="slug"
                value={formData.slug}
                onChange={handleChange}
                required
              />
            </div>

            <div className="mb-3 text-start">
  <label className="form-label">Content</label>
  <div ref={editorRef} style={{ minHeight: "200px", border: "1px solid #ccc" }}></div>
</div>


            <div className="mb-3 text-start">
              <label className="form-label">Author Name</label>
              <input
                type="text"
                className="form-control"
                name="author_name"
                value={formData.author_name}
                onChange={handleChange}
                required
              />
            </div>

            <div className="mb-3 text-start">
              <label className="form-label">Thumbnail Image</label>
              <input
                type="file"
                className="form-control"
                accept="image/*"
                onChange={handleFileChange}
              />
            </div>






            <div className="mb-3 text-start">
              <label className="form-label">Alt Text</label>
              <input
                type="text"
                className="form-control"
                name="alt_img" placeholder="Enter Alt text"
                value={formData.alt_img}
                onChange={handleChange}
                 
              />
            </div>




<h3 className="text-start">SEO Parameters</h3>

<div className="mb-3 text-start">
              <label className="form-label">Blog Title</label>
              <input
                type="text"
                className="form-control"
                name="title_tag" placeholder="Enter Title here"
                value={formData.title_tag}
                onChange={handleChange}
                 
              />
            </div>

            <div className="mb-3 text-start">
              <label className="form-label">Meta Description</label>
              <input
                type="text"
                className="form-control"
                name="meta_description" placeholder="Enter Meta Description"
                value={formData.meta_description}
                onChange={handleChange}
                 
              />
            </div>

            <div className="mb-3 text-start">
              <label className="form-label">Meta Keyword</label>
              <input
                type="text"
                className="form-control"
                name="meta_keywords" placeholder="Enter Meta Keyword"
                value={formData.meta_keywords}
                onChange={handleChange}
               />
            </div>

            <div className="mb-3 text-start">
              <label className="form-label">Meta Schema</label>
              <input
                type="text"
                className="form-control"
                name="schema_markup" placeholder="Enter Meta Schema"
                value={formData.schema_markup}
                onChange={handleChange}
                 
              />
            </div>








            <div className="form-check mb-3 text-start">
              <input
                className="form-check-input"
                type="checkbox"
                name="is_published"
                checked={formData.is_published}
                onChange={handleChange}
              />
              <label className="form-check-label">Publish Blog</label>
            </div>

            <div className="text-end">
              <button type="submit" className="btn submitbtnn">
                Update Blog
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default BlogEdit;
